import React, { useState, useEffect } from 'react';
import equipmentService from '../api/equipmentService';
// Paleta institucional Elecom — tokens compartidos (ver src/theme/colors.js).
// Antes el toggle de tipo de mantenimiento usaba un azul (#1890ff) ajeno a la
// paleta de marca — se reemplaza acá por el teal institucional.
import { COLORS as C } from '../theme/colors';

// Traducciones para el estado de la orden — solo se muestra el selector en modo edición,
// igual que WorkOrderForm.js (al crear, el backend decide el estado inicial).
const STATUS_OPTIONS = [
  { value: 'PENDING',     label: 'Pendiente' },
  { value: 'ASSIGNED',    label: 'Asignada' },
  { value: 'IN_PROGRESS', label: 'En progreso' },
  { value: 'COMPLETED',   label: 'Completada' },
  { value: 'CANCELED',    label: 'Cancelada' },
];

/**
 * @description Formulario en cascada para que ADMIN/SUPERVISOR creen o editen órdenes de
 * mantenimiento. El selector de equipos se filtra según el proyecto elegido. Al cambiar de
 * proyecto manualmente se resetea el equipo seleccionado para evitar inconsistencias en el
 * payload (esto no ocurre en la carga inicial de edición, solo ante un cambio del usuario).
 *
 * @param {object} props Las propiedades del componente.
 * @param {object}        [props.order]        Orden de mantenimiento a editar (detalle completo,
 *                                              de GET /maintenance-orders/:id). Si se omite, el
 *                                              formulario opera en modo creación.
 * @param {Array<object>} props.projectList    Lista de proyectos disponibles. Cada elemento: { project_id, name }.
 * @param {Array<object>} props.technicianList Lista de técnicos disponibles. Cada elemento: { user_id, username }.
 * @param {Function}      props.onSubmit       Callback que recibe el payload listo para enviar al backend.
 * @param {Function}      props.onCancel       Callback que se ejecuta al cancelar el formulario.
 * @param {boolean}       props.loading        Deshabilita el botón de envío mientras se procesa la petición.
 * @returns {JSX.Element}
 */
const MaintenanceOrderForm = ({
  order = null,
  projectList = [],
  technicianList = [],
  onSubmit,
  onCancel,
  loading,
}) => {
  // Estado principal del formulario — refleja los campos del payload final.
  // Si viene `order` (modo edición), se inicializa con sus valores; el campo `status`
  // solo se usa en edición (igual que WorkOrderForm.js).
  const [formData, setFormData] = useState({
    project_id: order?.project_id != null ? String(order.project_id) : '',
    equipment_id: order?.equipment_id != null ? String(order.equipment_id) : '',
    maintenance_type: order?.maintenance_type || 'PREVENTIVE',
    assigned_to: order?.assigned_to != null ? String(order.assigned_to) : '',
    // El backend devuelve scheduled_for como ISO datetime; el input type="date" espera yyyy-mm-dd
    scheduled_for: order?.scheduled_for ? order.scheduled_for.slice(0, 10) : '',
    notes: order?.notes || '',
    status: order?.status || 'PENDING',
  });

  // Lista completa de equipos cargada al montar — la filtración es local
  const [allEquipments, setAllEquipments] = useState([]);
  // Subconjunto de equipos que pertenecen al proyecto seleccionado
  const [filteredEquipments, setFilteredEquipments] = useState([]);
  // Indicador de carga de la lista de equipos
  const [loadingEquipments, setLoadingEquipments] = useState(true);

  // Carga todos los equipos una sola vez al montar el componente
  useEffect(() => {
    const fetchEquipments = async () => {
      try {
        const response = await equipmentService.getEquipments();
        setAllEquipments(response.data);
      } catch (error) {
        console.error('Error al cargar equipos:', error);
      } finally {
        setLoadingEquipments(false);
      }
    };

    fetchEquipments();
  }, []);

  // Filtra los equipos localmente cada vez que cambia el proyecto seleccionado
  useEffect(() => {
    if (!formData.project_id) {
      setFilteredEquipments([]);
      return;
    }

    const projectIdNum = Number(formData.project_id);
    const filtered = allEquipments.filter(
      (equipment) => equipment.project_id === projectIdNum
    );
    setFilteredEquipments(filtered);
  }, [formData.project_id, allEquipments]);

  /**
   * @description Manejador genérico para todos los campos del formulario.
   * Al cambiar el proyecto, resetea el equipo seleccionado antes de actualizar el estado.
   */
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'project_id') {
      // Cascada: cambiar proyecto limpia el equipo para evitar IDs huérfanos en el payload
      setFormData((prev) => ({ ...prev, project_id: value, equipment_id: '' }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * @description Alterna el tipo de mantenimiento entre PREVENTIVE y CORRECTIVE.
   * @param {'PREVENTIVE'|'CORRECTIVE'} type El tipo seleccionado.
   */
  const handleTypeToggle = (type) => {
    setFormData((prev) => ({ ...prev, maintenance_type: type }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validación de campos obligatorios antes de construir el payload
    if (!formData.project_id) {
      alert('Por favor, seleccione un proyecto.');
      return;
    }
    if (!formData.equipment_id) {
      alert('Por favor, seleccione un equipo.');
      return;
    }
    if (!formData.maintenance_type) {
      alert('Por favor, seleccione el tipo de mantenimiento.');
      return;
    }
    if (!formData.assigned_to) {
      alert('Por favor, asigne un técnico.');
      return;
    }

    // Construcción del payload — campos opcionales se omiten si están vacíos.
    // El estado solo se envía en edición (igual que WorkOrderForm.js): al crear,
    // el backend decide el estado inicial según si viene técnico asignado.
    const payload = {
      maintenance_type: formData.maintenance_type,
      project_id: Number(formData.project_id),
      equipment_id: Number(formData.equipment_id),
      assigned_to: Number(formData.assigned_to),
      ...(formData.scheduled_for && { scheduled_for: formData.scheduled_for }),
      ...(formData.notes.trim() && { notes: formData.notes.trim() }),
      ...(order && { status: formData.status }),
    };

    onSubmit(payload);
  };

  // Estilos del toggle de tipo de mantenimiento — patrón two-button side-by-side,
  // ahora con el teal de marca en vez del azul suelto que tenía antes.
  const getToggleButtonStyle = (isActive) => ({
    flex: 1,
    height: '40px',
    cursor: 'pointer',
    border: `1px solid ${isActive ? C.teal : 'rgba(56,62,65,0.2)'}`,
    backgroundColor: isActive ? C.teal : C.white,
    color: isActive ? C.white : C.charcoal,
    fontSize: '14px',
    fontWeight: isActive ? '600' : '500',
    transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
  });

  return (
    <form onSubmit={handleSubmit} style={styles.form}>

      {/* Selector de proyecto — primer nivel de la cascada */}
      <div className="modal-field-group">
        <label className="modal-field-label" htmlFor="mo-project">Proyecto</label>
        <select
          id="mo-project"
          name="project_id"
          value={formData.project_id}
          onChange={handleChange}
          required
          className="modal-field"
        >
          <option value="">Seleccione un proyecto</option>
          {projectList.map((project) => (
            <option key={project.project_id} value={project.project_id}>
              {project.name}
            </option>
          ))}
        </select>
      </div>

      {/* Selector de equipo — se habilita solo cuando hay un proyecto seleccionado */}
      <div className="modal-field-group">
        <label className="modal-field-label" htmlFor="mo-equipment">Equipo</label>
        {loadingEquipments ? (
          <p style={{ color: 'rgba(56,62,65,0.5)', fontSize: '14px', margin: 0 }}>
            Cargando equipos...
          </p>
        ) : (
          <select
            id="mo-equipment"
            name="equipment_id"
            value={formData.equipment_id}
            onChange={handleChange}
            required
            disabled={!formData.project_id}
            className="modal-field"
          >
            <option value="">
              {formData.project_id
                ? 'Seleccione un equipo'
                : 'Seleccione un proyecto primero'}
            </option>
            {filteredEquipments.map((equipment) => (
              <option key={equipment.equipment_id} value={equipment.equipment_id}>
                {equipment.name}
                {equipment.elevator_number
                  ? ` — Ascensor ${equipment.elevator_number}`
                  : ''}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Toggle de tipo de mantenimiento — PREVENTIVE | CORRECTIVE */}
      <div className="modal-field-group">
        <label className="modal-field-label">Tipo de mantenimiento</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={() => handleTypeToggle('PREVENTIVE')}
            style={{ ...getToggleButtonStyle(formData.maintenance_type === 'PREVENTIVE'), borderRadius: '8px' }}
          >
            Preventivo
          </button>
          <button
            type="button"
            onClick={() => handleTypeToggle('CORRECTIVE')}
            style={{ ...getToggleButtonStyle(formData.maintenance_type === 'CORRECTIVE'), borderRadius: '8px' }}
          >
            Correctivo
          </button>
        </div>
      </div>

      {/* Selector de técnico asignado */}
      <div className="modal-field-group">
        <label className="modal-field-label" htmlFor="mo-technician">Técnico asignado</label>
        <select
          id="mo-technician"
          name="assigned_to"
          value={formData.assigned_to}
          onChange={handleChange}
          required
          className="modal-field"
        >
          <option value="">Asignar técnico</option>
          {technicianList.map((technician) => (
            <option key={technician.user_id} value={technician.user_id}>
              {technician.username}
            </option>
          ))}
        </select>
      </div>

      {/* Selector de estado — solo visible en edición, mapea al enum MaintenanceStatus del backend */}
      {order && (
        <div className="modal-field-group">
          <label className="modal-field-label" htmlFor="mo-status">Estado</label>
          <select
            id="mo-status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="modal-field"
          >
            {STATUS_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      )}

      {/* DatePicker para fecha programada — campo opcional */}
      <div className="modal-field-group">
        <label className="modal-field-label" htmlFor="mo-scheduled-for">Fecha programada (opcional)</label>
        <input
          id="mo-scheduled-for"
          type="date"
          name="scheduled_for"
          value={formData.scheduled_for}
          onChange={handleChange}
          className="modal-field"
        />
      </div>

      {/* Textarea de notas — campo opcional */}
      <div className="modal-field-group">
        <label className="modal-field-label" htmlFor="mo-notes">Notas (opcional)</label>
        <textarea
          id="mo-notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          placeholder="Notas u observaciones adicionales..."
          rows={3}
          className="modal-field modal-field-textarea"
        />
      </div>

      {/* Pie de acciones — sticky al fondo del cuerpo scrolleable (Modal.js) */}
      <div className="modal-footer">
        <button type="button" onClick={onCancel} className="modal-btn-secondary">
          Cancelar
        </button>
        <button type="submit" className="modal-btn-primary" disabled={loading}>
          {loading ? 'Guardando...' : order ? 'Guardar Cambios' : 'Crear Orden'}
        </button>
      </div>

    </form>
  );
};

// El gap entre campos vive acá porque Modal.js solo controla el espaciado
// de su único hijo directo (el <form>).
const styles = {
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
};

export default MaintenanceOrderForm;
